export const isScheduleActive = (schedule, currentTime = new Date()) => {
  if (!schedule) return true;
  const scheduleBetweenDates = isTimeBetweenDates(
    currentTime,
    schedule.get('date_start'),
    schedule.get('date_end')
  );
  const daysScheduleActive = isDayScheduleActive(schedule.get('days'), currentTime);
  return scheduleBetweenDates && daysScheduleActive;
};

export const isTimeBetweenDates = (currentTime, startDate, endDate) => {
  if (!startDate && !endDate) return true; // no dates so time valid
  const startDateValid = new Date(currentTime).getTime() >= new Date(startDate).getTime();
  const endDateValid = new Date(currentTime).getTime() < new Date(endDate).getTime();
  if (!startDate) return endDateValid; // no start date, so just validate end date
  if (!endDate) return startDateValid; // no end date, so just validate start date
  return startDateValid && endDateValid; // validate both dates
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const isDayScheduleActive = (days, currentTime) => {
  if (!days || days.size < 1) return true; // schedule is active if there are no day schedules set
  // First check today's windows, then any cross-midnight window belonging to
  // the previous day whose tail extends past midnight into the current day.
  return isCurrentDayActive(days, currentTime) || isPreviousDayOvernightActive(days, currentTime);
};

const isCurrentDayActive = (days, currentTime) => {
  const currentDaySchedule = getCurrentDaySchedule(days, currentTime);
  if (!currentDaySchedule) return false; // return if no schedule for current day
  const timesAvailable = currentDaySchedule.get('times').find(time => {
    const startTime = timeToDate(time.get('start_time'), currentTime);
    let endTime = timeToDate(time.get('end_time'), currentTime);
    // end_time <= start_time means the window crosses midnight (e.g. 22:00–02:00).
    if (endTime.getTime() <= startTime.getTime()) {
      endTime = new Date(endTime.getTime() + DAY_IN_MS);
    }
    return isTimeBetweenDates(currentTime, startTime, endTime);
  });
  return !!timesAvailable;
};

const isPreviousDayOvernightActive = (days, currentTime) =>
  getPreviousDayOvernightEnd(days, currentTime) !== false;

// A window stored on the previous weekday that crosses midnight (e.g. 22:00–02:00
// on Thursday) is still active in the early hours of the current day. Anchor the
// window on the previous day and, if currentTime falls within its post-midnight
// tail, return that tail's end instant (timestamp); otherwise return false.
const getPreviousDayOvernightEnd = (days, currentTime) => {
  const previousDay = addDays(currentTime, -1); // day before, normalised to 00:00
  const currentWeekday = getCurrentDay(currentTime);
  const previousWeekday = currentWeekday === 1 ? 7 : currentWeekday - 1; // Monday(1) wraps to Sunday(7)
  const previousDaySchedule = days.find(day => day.get('day') === previousWeekday);
  if (!previousDaySchedule) return false;
  let tailEnd = false;
  previousDaySchedule.get('times').find(time => {
    const startTime = timeToDate(time.get('start_time'), previousDay);
    const endTime = timeToDate(time.get('end_time'), previousDay);
    // Only cross-midnight windows can have a tail in the current day.
    if (endTime.getTime() > startTime.getTime()) return false;
    const extendedEnd = new Date(endTime.getTime() + DAY_IN_MS);
    if (isTimeBetweenDates(currentTime, startTime, extendedEnd)) {
      tailEnd = extendedEnd.getTime();
      return true;
    }
    return false;
  });
  return tailEnd;
};

const getCurrentDay = currentTime => (currentTime.getDay() === 0 ? 7 : currentTime.getDay()); // javascript day for Sunday is 0 but API sends it as 7 so need to convert it

const getCurrentDaySchedule = (days, currentTime) => {
  const currentDay = getCurrentDay(currentTime);
  const currentDaySchedule = days.find(day => day.get('day') === currentDay);
  return currentDaySchedule || false;
};

export const timeToDate = (timeString, date = new Date()) => {
  const newDate = new Date(date.valueOf());
  const timeParts = timeString.split(':');
  newDate.setHours(timeParts[0]);
  newDate.setMinutes(timeParts[1]);
  newDate.setSeconds(timeParts[2]);
  return newDate;
};

export const getNextActiveTime = (schedule, currentTime = new Date()) => {
  if (!schedule) return false;

  const scheduleStartTime = new Date(schedule.get('date_start'));
  const scheduleFinished = !isTimeBetweenDates(currentTime, null, schedule.get('date_end'));
  const scheduleNotStarted = !isTimeBetweenDates(currentTime, schedule.get('date_start'));

  if (scheduleFinished) return false; // schedule is past end date

  if (scheduleNotStarted) currentTime = scheduleStartTime;

  const nextTime = getNextTimeFromDays(schedule.get('days'), currentTime) || scheduleStartTime.getTime();
  const nextTimeIsInSchedule = isTimeBetweenDates(
    nextTime,
    schedule.get('date_start'),
    schedule.get('date_end')
  );

  // We have to check if the time found is in schedule as the schedule could, for example,
  // end tomorrow morning at 9am but the next time would be 10am.
  return nextTimeIsInSchedule ? nextTime : false;
};

const getNextTimeFromDays = (days, currentTime, timeType = 'start_time') => {
  let nextTime = false;
  if (days && days.size) {
    const currentDaySchedule = getCurrentDaySchedule(days, currentTime);
    if (currentDaySchedule) {
      nextTime = getNextTime(currentDaySchedule.get('times'), currentTime, timeType);
    }

    if (!nextTime) {
      // if no time found for current day, check future days in schedule
      const currentWeekday = getCurrentDay(currentTime);
      const sortedDays = sortDays(days, currentWeekday);
      sortedDays.find(day => {
        const dayDiff =
          day.get('day') < currentWeekday
            ? day.get('day') + 7 - currentWeekday
            : day.get('day') - currentWeekday;
        const nextDate = addDays(currentTime, dayDiff);
        nextTime = getNextTime(day.get('times'), nextDate, timeType);
        return nextTime;
      });
    }
  }
  return nextTime;
};

// Resolve a time-of-day for a given time range, shifting end_time forward 24h
// when the window crosses midnight (end_time <= start_time, e.g. 22:00–02:00).
const resolveTime = (time, currentTime, compareTimeType) => {
  let target = timeToDate(time.get(compareTimeType), currentTime).getTime();
  if (compareTimeType === 'end_time') {
    const startTime = timeToDate(time.get('start_time'), currentTime).getTime();
    if (target <= startTime) target += 24 * 60 * 60 * 1000;
  }
  return target;
};

const getNextTime = (times, currentTime, compareTimeType = 'start_time') => {
  const sortedTimes = sortTimes(times, currentTime);
  const nextTimeRange = sortedTimes.find(
    time => resolveTime(time, currentTime, compareTimeType) >= currentTime.getTime() // compare equal times in case schedule set to start at midnight
  );
  return nextTimeRange ? resolveTime(nextTimeRange, currentTime, compareTimeType) : false;
};

export const getInactiveTime = (schedule, currentTime = new Date()) => {
  // get time that schedule is next inactive
  if (!schedule) return false;
  if (isScheduleActive(schedule, currentTime)) {
    // If active because of a previous day's cross-midnight window, the next
    // inactive instant is that window's tail end (e.g. Fri 02:00), not a future
    // day's end_time which getNextTimeFromDays (looking up today) would return.
    const overnightTailEnd = getPreviousDayOvernightEnd(schedule.get('days'), currentTime);
    if (overnightTailEnd !== false && !isCurrentDayActive(schedule.get('days'), currentTime)) {
      const tailEndDate = schedule.get('date_end') && new Date(schedule.get('date_end')).getTime();
      if (tailEndDate && tailEndDate < overnightTailEnd) return tailEndDate;
      return overnightTailEnd;
    }
    const nextTime = getNextTimeFromDays(schedule.get('days'), currentTime, 'end_time');
    const endDate = schedule.get('date_end') && new Date(schedule.get('date_end')).getTime();
    if (endDate && (!nextTime || endDate < nextTime)) return endDate; // if end date present and sooner than the date found or no other date found
    return nextTime || false;
  }
  return currentTime.getTime();
};

export const sortTimes = (times, now) =>
  times.sort((a, b) =>
    timeToDate(a.get('start_time'), now).getTime() < timeToDate(b.get('start_time'), now).getTime() ? -1 : 1
  );

export const addDays = (date, days) => {
  if (days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    result.setHours(0);
    result.setMinutes(0);
    result.setSeconds(0);
    return result;
  }
  return new Date(date);
};

// Sort days from the day after the current weekday increasing and wrapping
// around after sunday, as we only want to go through the days
export const sortDays = (days, weekdayNow) =>
  days.sort((day1, day2) => {
    const sortableId = day => {
      const adjustedDayId = day.get('day') - weekdayNow;
      return adjustedDayId < 1 ? adjustedDayId + 7 : adjustedDayId;
    };
    return sortableId(day1) < sortableId(day2) ? -1 : 1;
  });
